import { useSession } from "next-auth/react";
import { useCallback, useEffect, useState } from "react";
import httpErrorMessage from "@/lib/http-error-message";
import apiFetch from "@/lib/fetch";

interface QueryApiProps {
  path: string;
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH" | "OPTIONS" | "HEAD";
  body?: any;
  auth?: boolean;
  doNotFirstFetch?: boolean;
}

interface MutationApiProps {
  path: string;
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH" | "OPTIONS" | "HEAD";
  body?: any;
  auth?: boolean;
}

interface MutationApiOutput {
  loading: boolean;
  error: boolean;
  sendRequest: (body?: any) => Promise<any>;
}

export function useDataApi({
  path,
  method,
  body,
  auth,
  doNotFirstFetch,
}: QueryApiProps) {
  const { data: sessionData, status } = useSession();

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [error, setError] = useState(false);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [data, setData] = useState<any>(null);

  // @ts-ignore
  const token = sessionData?.token;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const fetchData = useCallback(async () => {
    const response = await apiFetch({
      route: path,
      headers: {
        Authorization: `Bearer ${token}`,
      },
      method: method,
      body: body ? JSON.stringify(body) : undefined,
      handleErrorSilently: false,
    });

    if (response.error == true) {
      httpErrorMessage(response);
      setLoading(false);
      setError(true);

      return null;
    }

    setData(response);
    setLoading(false);

    return response;
  }, [body, method, path, token]);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (auth && status === "loading") return;
    if (auth && status === "unauthenticated") return;
    if (!doNotFirstFetch) {
      fetchData();
    }
  }, [fetchData, auth, status, doNotFirstFetch]);

  const refetch = () => {
    fetchData();
  };

  if (auth && status === "loading")
    return { loading: true, error: false, data: null };
  if (auth && status === "unauthenticated")
    return { loading: false, error: true, data: null };

  return { loading, error, data, sessionData, refetch };
}

export function useDataUpdater({
  path,
  method,
  body,
  auth,
}: MutationApiProps): MutationApiOutput {
  const { sessionData, status } = getSessionData();
  // if (auth && status === 'loading') return {
  //     loading: true,
  //     error: false,
  //     sendRequest: () => Promise.resolve(null)
  // }
  // if (auth && status === 'unauthenticated') return {
  //     loading: false,
  //     error: true,
  //     sendRequest: () => Promise.resolve(null)
  // }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [loading, setLoading] = useState(false);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [error, setError] = useState(false);

  // @ts-ignore
  const token = sessionData?.token;

  const sendRequest = async (customBody = {}): Promise<any> => {
    setLoading(true);
    setError(false);

    const response = await apiFetch({
      route: path,
      headers: {
        Authorization: `Bearer ${token}`,
      },
      method: method,
      body: Object.keys(customBody || {})?.length > 0 ? customBody : body,
      handleErrorSilently: false,
    });

    if (response.error == true) {
      httpErrorMessage(response);
      setLoading(false);
      setError(true);

      return null;
    }

    setLoading(false);
    setError(false);

    return response;
  };

  return { loading, error, sendRequest };
}

function getSessionData() {
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { data: sessionData, status } = useSession();
    if (status === "loading") return { sessionData: null, status };
    if (status === "unauthenticated") return { sessionData: null, status };
    return { sessionData, status };
  } catch (e) {
    return { sessionData: null, status: "unauthenticated" };
  }
}
